// Bộ primitive UI của HiDental NAY DÙNG CHUNG từ kit Họ A @suga-co/ui (vendor v0.4).
// ----------------------------------------------------------------------------------
// Trước đây đây là code riêng của hidental; nay kit @suga-co/ui ĐÃ là bản hợp nhất
// (mirror hidental + nâng cấp: token-hoá đỏ/xanh, a11y, alias tương thích ngược).
// → File này chỉ RE-EXPORT để 192 nơi `import ... from "@/components/ui"` GIỮ NGUYÊN,
//   nhưng giờ chạy bằng component dùng chung → "sửa 1 nơi áp mọi nơi".
//
// API tương thích 100% (kit là superset): Button/Badge nhận thêm alias cũ, Card thêm
// prop `title`, Table thêm `columns/rows`. Màu trạng thái (destructive/Badge đỏ-xanh)
// nay theo token --danger/--ok/--warn/--info (đã khai trong globals.css + tailwind.config).
//
// ⛳ 21/09/2026 — `Table` KHÔNG còn là re-export thuần, nó được BỌC ở cuối tệp này để đổi tông
//    sọc xen kẽ cho cả HiDental. Lý do + cách đo: xem khối chú thích ngay trên `Table` bên dưới.
import * as React from "react";
import { Table as BangKit, type Column } from "@suga-co/ui";
import { cn } from "@/lib/utils";

export type { Column };

export {
  Button,
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  Badge,
  inputCls,
  Input,
  Textarea,
  Label,
  Select,
  Field,
  PageHeader,
  Stat,
  Th,
  Td,
  Skeleton,
  Empty,
  InfoBox,
  // Hướng dẫn TRONG APP (kit Họ A): hộp Guide mỗi màn · HelpPanel gập-nhớ · HintLabel tooltip · Tour làm quen.
  Guide,
  HelpPanel,
  HintLabel,
  Tour,
  type TourStep,
  // Nút trong <form action={serverAction}> — RA SOÁT 28/07 phát hiện kit ĐÃ CÓ SẴN cả hai
  // từ lâu mà file này quên re-export ⇒ 39 nút Xóa hỏi-không-thèm-hỏi và ~70 nút Lưu bấm
  // 2 lần ghi 2 bản ghi, trong khi thuốc chữa nằm ngay trong node_modules.
  //   • SubmitButton  — tự khóa + chấm xoay khi server action đang chạy (chống bấm đúp).
  //   • ConfirmButton — như trên, có HỎI LẠI trước khi chạy (thao tác xóa/hủy).
  // ⚠️ Cả hai là client component ("use client") nhưng ĐẶT ĐƯỢC trong <form> của Server
  //    Component — form vẫn là server, chỉ cái nút là client. Không cần đổi trang thành client.
  SubmitButton,
  ConfirmButton,
} from "@suga-co/ui";

// ════════════════════════════════════════════════════════════════════════════
// TABLE — BỌC LẠI ĐỂ ĐỔI TÔNG SỌC (vé `A6` + vế sọc `A2` + vế màu `B3`, đợt OPS 21/09/2026)
//
// 🩸 VÌ SAO: sọc xen kẽ của kit là `bg-muted/20`, mà `--muted` = `40 24% 95%` ⇒ ở độ đục 20% trên
//    nền trắng nó ra **≈99% sáng, gần như vô hình**. Nhà mình đã kêu đúng con số này BA lần: mục ⑥
//    tờ 12/08 (màn lịch hẹn), vé `A6` 21/09 (lịch sử thanh toán: *"yêu cầu cho màu xen kẽ trắng
//    vàng hoặc trắng xám nhẹ"*), vé `B3` 21/09 (màn KHĐT: *"hiện tại toàn màu trắng"*). Ba lần cùng
//    một gốc ⇒ chữa ở gốc, một lần, cho cả nhà.
//
// ⛳ AN CHỐT 21/09/2026 (chiều) — SỬA Ở LỚP NÀY, KHÔNG SỬA KIT. Tờ phân loại §3 hàng 1. Vì sao lớp
//    này là đúng chỗ: `@suga-co/ui` còn cõng SugaHR và SugaLegal, đổi tông trong kit là đổi cho hai
//    phần mềm chưa ai xin. Còn tệp này là cửa DUY NHẤT 192 nơi trong HiDental đi qua ⇒ đè ở đây là
//    "sửa 1 nơi áp mọi nơi" đúng nghĩa, mà biên giới dừng đúng ở HiDental.
//
// 🔬 ĐO THẬT, KHÔNG SUY LUẬN (chạy `twMerge` của chính kit, 21/09): kit gộp `className` bằng
//    `cn()` = `twMerge(clsx(...))`, và `twMerge` coi `[&_tbody_tr:nth-child(even)]:bg-muted/20` với
//    `[&_tbody_tr:nth-child(even)]:bg-warn/[0.07]` là CÙNG một ô (cùng biến thể, cùng nhóm
//    `bg-color`) ⇒ lớp truyền vào **THAY**, không phải chồng thêm. Chuỗi ra:
//      w-full text-sm … [&_tbody_tr:hover]:bg-brand/[0.06] … [&_tbody_tr:nth-child(even)]:bg-warn/[0.07]
//    — `bg-muted/20` BIẾN MẤT khỏi chuỗi. Đây là điều kiện để cách làm này chạy; nâng kit lên bản
//    khác mà `cn()` đổi sang `clsx` thuần thì hai lớp sẽ chồng nhau và sọc quay về gần-trắng.
//
// 🎨 TÔNG: `bg-warn/[0.07]` — `--warn` = `38 92% 50%` (hổ phách) ở 7% đục. Chọn 7% chứ không 10%+
//    vì bảng nào cũng có phù hiệu vàng (`Badge tone="amber"`, nút `bg-warn`) đứng trong ô; sọc đậm
//    hơn là hai sắc vàng cãi nhau. Đúng chữ OPS xin: *"xen kẽ trắng và vàng nhạt"*.
//
// 🩸 VÁ KÈM — HÀNG CHẴN RÊ CHUỘT KHÔNG ĐỔI MÀU, một bọ CÓ SẴN của kit mà lượt này BUỘC phải chữa.
//    Kit vẽ hover `[&_tbody_tr:hover]:bg-brand/[0.06]` = (0,2,2), NGANG độ cụ thể với luật sọc chẵn
//    của chính nó, mà luật sọc chẵn nằm SAU trong sheet ⇒ hàng CHẴN thắng, tức rê chuột vào hàng
//    chẵn KHÔNG hề đổi màu. Đo thật 03/09/2026 trên trang chạy, đã ghi ở
//    `app/appointments/AppointmentRow.tsx`. Sáu tuần không ai kêu vì `bg-muted/20` gần như vô hình
//    — nửa bảng chết hover mà nhìn không ra. Đổi sang hổ phách thấy được là bọ ấy LỘ NGAY ⇒ không
//    được để nó đi cùng chuyến. `[&_tbody_tr:nth-child(even):hover]` = `.x tbody tr:nth-child(even)
//    :hover` = **(0,3,2)** > (0,2,2) ⇒ thắng chắc cả hai luật trên, không phụ thuộc thứ tự sheet.
//
// 📌 THỨ TỰ GỘP CỐ Ý: tông nhà đứng TRƯỚC `className` của nơi gọi ⇒ màn nào cần tông khác (hoặc
//    cần TẮT hẳn sọc để tự tô theo NHÓM, như bảng Thu ngân và màn Lịch hẹn) chỉ việc truyền lớp
//    cùng ô, `twMerge` cho nó thắng. Dùng hằng `TAT_SOC_KIT` bên dưới cho ca tắt.
const SOC_NHA =
  "[&_tbody_tr:nth-child(even)]:bg-warn/[0.07] [&_tbody_tr:nth-child(even):hover]:bg-brand/[0.06]";

/**
 * TẮT sọc xen kẽ của bảng — dùng cho bảng TỰ TÔ lấy (theo chỉ số PHIẾU, theo số thứ tự dòng…).
 *
 * ⚠️ Lớp này sinh ra `.<bảng> tbody tr:nth-child(even){background:transparent}` — độ cụ thể
 *    **(0,2,2)**, tức vẫn CAO HƠN một lớp `bg-…` đeo thẳng trên `<tr>` (0,1,0). Nên bảng nào dùng
 *    `TAT_SOC_KIT` thì lớp tô hàng của nó phải mang `!` (`!bg-warn/[0.07]`), đúng nếp đã tính tay
 *    và ghi lại ở `app/appointments/AppointmentRow.tsx` (khối ⛳ 21/09). Có `!` rồi thì hover cũng
 *    phải `!` (`hover:!bg-…`) — giữa hai khai báo `!important`, độ cụ thể mới phân thắng bại, và
 *    `.<cls>:hover` (0,2,0) thắng `.<cls>` (0,1,0) ⇒ rê chuột vẫn đổi màu.
 *
 * 📌 Lớp này CHỈ tắt cái NỀN sọc; luật vá hover hàng chẵn của `SOC_NHA` cố ý ở lại. Nó vô hại:
 *    hàng nào có lớp tô `!` thì `!` thắng nó, hàng nào không thì nó vẽ đúng tông hover chung.
 */
export const TAT_SOC_KIT = "[&_tbody_tr:nth-child(even)]:bg-transparent";

type PropsBang<Row> = React.TableHTMLAttributes<HTMLTableElement> & {
  columns?: Column<Row>[];
  rows?: Row[];
  /** Đầu bảng DÍNH khi cuộn (cần đặt bảng trong khung có chiều cao giới hạn — xem `khungCuon`). */
  stickyHeader?: boolean;
  /**
   * Bọc bảng trong KHUNG CUỘN DỌC cao tối đa 65vh. Đi CẶP với `stickyHeader`.
   *
   * 🩸 VÌ SAO PHẢI CÓ (đo 21/09/2026, vé `A6`): `stickyHeader` của kit chỉ đeo
   *    `[&_thead_th]:sticky [&_thead_th]:top-0 [&_thead_th]:z-10` lên `<th>`. Không có khung cao
   *    giới hạn thì chỗ cuộn của nó là CẢ TRANG ⇒ đầu bảng trôi lên bám mốc 0 của màn hình, tức
   *    chui thẳng vào chỗ header shell đang đứng (`sticky top-0 z-10`). Hai bên CÙNG `z-10` và
   *    `<table>` nằm SAU trong DOM ⇒ đầu bảng che mất ô tìm kiếm + ô chọn chi nhánh của shell.
   *    Đúng con bệnh mà chú thích 🚫 đầu `app/patients/[id]/PatientHeader.tsx` kể lại (bản cũ để
   *    `z-30` nên che luôn header shell), chỉ khác lối vào.
   *    Có khung rồi thì chỗ cuộn là chính cái khung ⇒ đầu bảng KHÔNG bao giờ rời khỏi thẻ, không
   *    đụng khối dính `PatientTabs` (`sticky top-14 z-20`) lẫn shell — hết chuyện đếm z-index.
   *
   * ⚠️ `[&>div]:…` chứ không phải đeo thẳng lên thẻ này: kit TỰ dựng một `<div overflow-x-auto>`
   *    bọc `<table>`, và `overflow-x:auto` làm `overflow-y` tính ra `auto` ⇒ chính cái div ĐÓ mới
   *    là chỗ cuộn mà `position:sticky` nhìn vào. Đặt `max-h` ở thẻ ngoài là div trong không bao
   *    giờ tràn dọc, và đầu bảng đứng im như chưa bật gì.
   */
  khungCuon?: boolean;
};

export function Table<Row = unknown>({ className, khungCuon, ...props }: PropsBang<Row>) {
  const bang = <BangKit<Row> className={cn(SOC_NHA, className)} {...props} />;
  if (!khungCuon) return bang;
  return <div className="[&>div]:max-h-[65vh] [&>div]:overflow-y-auto">{bang}</div>;
}

/**
 * LỚP NÚT cho một `<Link>` / `<a>` — ⛳ 29/09/2026 (feature docs/feature/bang-rong-cuon-trang.md §3 "cùng lượt").
 *
 * 🩸 VÌ SAO: kit `Button` không có `asChild`, nên nút điều hướng khắp kho đang là `<Link><Button>` — thẻ bấm
 *    LỒNG thẻ bấm (HTML không hợp lệ) và HAI điểm dừng Tab cho một hành động (audit cụm Báo cáo §2.1).
 *    Ở đây trả CHUỖI LỚP y hệt `Button` của kit để đeo thẳng lên thẻ link ⇒ một thẻ, một điểm dừng Tab.
 * ⚠️ Chuỗi chép từ kit (`node_modules/@suga-co/ui/src/components.tsx`, `Button`) — lưới
 *    `test/bang-rong.test.tsx` so với HTML `Button` kit dựng ra, kit đổi lớp là lưới đỏ.
 *    Đẩy `asChild` vào kit là vé sau (feature §4: không đổi kit trong vé này).
 */
export function lopNut(
  variant: "default" | "outline" | "ghost" | "subtle" | "destructive" = "default",
  size: "sm" | "md" | "lg" | "icon" = "md",
): string {
  const bien = {
    default: "bg-brand text-brand-fg shadow-sm hover:brightness-110 hover:shadow-brand",
    outline: "border bg-card shadow-xs hover:bg-muted hover:border-brand/40",
    ghost: "hover:bg-muted",
    subtle: "bg-muted text-fg hover:brightness-95",
    destructive: "bg-danger/10 text-danger hover:bg-danger/15",
  }[variant];
  const co = { sm: "h-8 px-3 text-xs", md: "h-9 px-4 text-sm", lg: "h-11 px-6 text-sm", icon: "h-9 w-9" }[size];
  return cn(
    "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md font-medium transition-all duration-150 active:scale-[.98] disabled:opacity-50 disabled:pointer-events-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/40 focus-visible:ring-offset-2 focus-visible:ring-offset-bg",
    bien,
    co,
  );
}
